import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  User, Mail, Phone, Calendar, MapPin, Award, Trophy, 
  Video, Mic, FileText, BookOpen, Clock, CheckCircle, 
  XCircle, ChevronLeft, Play, Eye, Download, Loader2,
  GraduationCap, Target, Flame, Star, Shield, Activity,
  History, FileCheck, MessageSquare, AlertCircle, Headphones,
  BarChart2, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const StudentProfileViewer = ({ studentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (studentId) {
      loadStudentProfile();
    }
  }, [studentId]);

  const loadStudentProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/student/${studentId}/full-profile`, { 
        withCredentials: true 
      });
      setData(response.data);
    } catch (error) {
      console.error('Error loading student profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-gray-600">Öğrenci profili yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Öğrenci bulunamadı</p>
          <Button onClick={onClose} className="mt-4">Kapat</Button>
        </div>
      </div>
    );
  }

  const { student, cabin_progress, cabin_training, english_progress, cabin_lessons,
    listening_attempts, speaking_recordings, video_recordings, student_documents, agreements, exam_results, user_activities,
    login_history, certificates, feedback, daily_tasks, advisor_messages, stats } = data;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { 
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/20">
                <ChevronLeft className="w-5 h-5 mr-2" />
                Geri
              </Button>
              <div className="flex gap-2">
                {student.is_frozen && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-400">
                    ❄️ Dondurulmuş
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  student.last_login ? 'bg-green-500' : 'bg-orange-500'
                }`}>
                  {student.last_login ? 'Aktif' : 'Hiç Girmedi'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center text-4xl font-bold">
                {student.name?.charAt(0) || 'Ö'}
              </div>
              <div>
                <h1 className="text-3xl font-bold">{student.name || 'İsimsiz Öğrenci'}</h1>
                <div className="flex items-center gap-4 mt-2 text-blue-100">
                  <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {student.email}</span>
                  {student.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {student.phone}</span>}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full text-sm">
                    <Trophy className="w-4 h-4" /> {english_progress?.miles || cabin_progress?.miles || 0} Mil
                  </span>
                  <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full text-sm">
                    <Flame className="w-4 h-4" /> {english_progress?.streak || student.streak || 0} Gün Seri
                  </span>
                  <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full text-sm">
                    <Calendar className="w-4 h-4" /> Kayıt: {formatDate(student.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="p-6">
            <TabsList className="grid grid-cols-8 gap-2 mb-6">
              <TabsTrigger value="overview" className="text-xs">Genel Bakış</TabsTrigger>
              <TabsTrigger value="activities" className="text-xs">Aktiviteler</TabsTrigger>
              <TabsTrigger value="exams" className="text-xs">Sınavlar</TabsTrigger>
              <TabsTrigger value="history" className="text-xs">Geçmiş</TabsTrigger>
              <TabsTrigger value="recordings" className="text-xs">Kayıtlar</TabsTrigger>
              <TabsTrigger value="documents" className="text-xs">Belgeler</TabsTrigger>
              <TabsTrigger value="agreements" className="text-xs">Sözleşmeler</TabsTrigger>
              <TabsTrigger value="info" className="text-xs">Bilgiler</TabsTrigger>
            </TabsList>

            {/* GENEL BAKIŞ */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Activity className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-700">{stats?.total_activities || 0}</p>
                    <p className="text-sm text-blue-600">Toplam Aktivite</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <Headphones className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-700">{stats?.total_listening || 0}</p>
                    <p className="text-sm text-purple-600">Dinleme Denemesi</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 text-center">
                    <Mic className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-orange-700">{stats?.total_speaking || 0}</p>
                    <p className="text-sm text-orange-600">Konuşma Kaydı</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700">{stats?.total_exams || 0}</p>
                    <p className="text-sm text-green-600">Sınav</p>
                  </CardContent>
                </Card>
              </div>

              {/* İlerleme Kartları */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* İngilizce İlerleme */}
                {english_progress && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <BookOpen className="w-5 h-5 text-blue-500" /> İngilizce İlerlemesi
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Seviye:</span>
                          <span className="font-bold">{english_progress.current_level || student.current_level || 'A1'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mil:</span>
                          <span className="font-bold text-amber-600">{english_progress.miles || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Seri:</span>
                          <span className="font-bold text-orange-600">{english_progress.streak || 0} gün</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tamamlanan Ders:</span>
                          <span className="font-bold">{english_progress.completed_lessons?.length || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Kabin İlerleme */}
                {cabin_progress && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <GraduationCap className="w-5 h-5 text-purple-500" /> Kabin Eğitimi
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mil:</span>
                          <span className="font-bold text-amber-600">{cabin_progress.miles || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tamamlanan Ders:</span>
                          <span className="font-bold">{cabin_progress.completed_lessons?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Son Aktivite:</span>
                          <span className="text-sm">{formatDate(cabin_progress.last_activity)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Son Sınav Sonuçları */}
              {exam_results?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" /> Son Sınav Sonuçları
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {exam_results.slice(0, 5).map((exam, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{exam.exam_type || 'Sınav'}</p>
                            <p className="text-xs text-gray-500">{formatDate(exam.timestamp)}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full font-bold ${
                              (exam.score || 0) >= 70 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              %{exam.score || 0}
                            </span>
                            {exam.level && <p className="text-xs text-gray-500 mt-1">Seviye: {exam.level}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* AKTİVİTELER */}
            <TabsContent value="activities">
              <div className="space-y-6">
                {/* Son Aktiviteler */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" /> Son Aktiviteler ({user_activities?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-96 overflow-auto">
                    {user_activities?.length > 0 ? (
                      <div className="space-y-2">
                        {user_activities.map((activity, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Activity className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{activity.action || activity.page}</p>
                              <p className="text-xs text-gray-500">{activity.page}</p>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(activity.timestamp)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">Henüz aktivite kaydı yok</p>
                    )}
                  </CardContent>
                </Card>

                {/* Dinleme Denemeleri */}
                {listening_attempts?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Headphones className="w-5 h-5 text-purple-500" /> Dinleme Denemeleri ({listening_attempts.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-64 overflow-auto">
                      <div className="space-y-2">
                        {listening_attempts.map((attempt, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{attempt.question_id || `Soru ${idx + 1}`}</p>
                              <p className="text-xs text-gray-500">{formatDate(attempt.created_at)}</p>
                            </div>
                            {attempt.score !== undefined && (
                              <span className={`px-2 py-1 rounded text-sm font-bold ${
                                attempt.score >= 70 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                %{attempt.score}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Konuşma Kayıtları */}
                {speaking_recordings?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mic className="w-5 h-5 text-orange-500" /> Konuşma Kayıtları ({speaking_recordings.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-64 overflow-auto">
                      <div className="space-y-2">
                        {speaking_recordings.map((rec, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                            <Mic className="w-5 h-5 text-orange-600" />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{rec.question_text || rec.level || 'Kayıt'}</p>
                              <p className="text-xs text-gray-500">{formatDate(rec.created_at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* SINAVLAR */}
            <TabsContent value="exams">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" /> Tüm Sınav Sonuçları ({exam_results?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {exam_results?.length > 0 ? (
                    <div className="space-y-3">
                      {exam_results.map((exam, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              (exam.score || 0) >= 70 ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              <Target className={`w-6 h-6 ${
                                (exam.score || 0) >= 70 ? 'text-green-600' : 'text-red-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-bold">{exam.exam_type || 'Sınav'}</p>
                              <p className="text-sm text-gray-500">{formatDate(exam.timestamp)}</p>
                              {exam.correct_answers !== undefined && (
                                <p className="text-xs text-gray-400">
                                  {exam.correct_answers}/{exam.total_questions} doğru
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-3xl font-black ${
                              (exam.score || 0) >= 70 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              %{exam.score || 0}
                            </span>
                            {exam.level && (
                              <p className="text-sm text-gray-500">Seviye: {exam.level}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Henüz sınav sonucu yok</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* GEÇMİŞ */}
            <TabsContent value="history">
              <div className="grid grid-cols-2 gap-6">
                {/* Giriş Geçmişi */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" /> Giriş Geçmişi ({stats?.total_logins || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-80 overflow-auto">
                    {login_history?.length > 0 ? (
                      <div className="space-y-2">
                        {login_history.map((login, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <div className="flex-1">
                              <p className="text-sm">{formatDate(login.created_at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Giriş kaydı yok</p>
                    )}
                  </CardContent>
                </Card>

                {/* Günlük Görevler */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5" /> Günlük Görevler
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-80 overflow-auto">
                    {daily_tasks?.length > 0 ? (
                      <div className="space-y-2">
                        {daily_tasks.map((task, idx) => (
                          <div key={idx} className="p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">{task.date}</p>
                              <span className={`text-xs px-2 py-1 rounded ${
                                task.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {task.completed ? 'Tamamlandı' : 'Bekliyor'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Günlük görev kaydı yok</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sertifikalar */}
              {certificates?.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-500" /> Sertifikalar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {certificates.map((cert, idx) => (
                        <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <Award className="w-5 h-5 text-amber-500" />
                          <div>
                            <span className="font-medium">{cert.program_name || cert.certificate_id}</span>
                            <p className="text-xs text-gray-500">{formatDate(cert.issued_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* KAYITLAR - Ses ve Video */}
            <TabsContent value="recordings">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ses Kayıtları */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mic className="w-5 h-5 text-orange-500" />
                      Ses Kayıtları ({speaking_recordings?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {speaking_recordings?.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {speaking_recordings.map((rec, idx) => (
                          <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">{rec.title || rec.question_text?.substring(0, 50) || 'Ses Kaydı'}</span>
                              <span className="text-xs text-gray-500">{formatDate(rec.created_at)}</span>
                            </div>
                            {rec.score && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded">
                                  Puan: {rec.score}/100
                                </span>
                              </div>
                            )}
                            {(rec.audio_url || rec.audio_data) && (
                              <audio controls className="w-full h-8" src={rec.audio_url || rec.audio_data}>
                                Tarayıcınız ses oynatmayı desteklemiyor
                              </audio>
                            )}
                            {rec.feedback && (
                              <p className="text-xs text-gray-600 mt-2 italic">"{rec.feedback}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Henüz ses kaydı yok</p>
                    )}
                  </CardContent>
                </Card>

                {/* Video Kayıtları */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Video className="w-5 h-5 text-purple-500" />
                      Video Kayıtları ({video_recordings?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {video_recordings?.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {video_recordings.map((rec, idx) => (
                          <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">{rec.question || 'AI Mülakat Kaydı'}</span>
                              <span className="text-xs text-gray-500">{formatDate(rec.created_at)}</span>
                            </div>
                            {rec.score && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
                                  Puan: {rec.score}/100
                                </span>
                              </div>
                            )}
                            {rec.video_url && (
                              <video controls className="w-full rounded" style={{maxHeight: '200px'}}>
                                <source src={rec.video_url} type="video/webm" />
                                Tarayıcınız video oynatmayı desteklemiyor
                              </video>
                            )}
                            {rec.ai_feedback && (
                              <p className="text-xs text-gray-600 mt-2 italic">"{rec.ai_feedback}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Henüz video kaydı yok</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* BELGELER */}
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    Yüklenen Belgeler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {student_documents ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Fotoğraf */}
                      <div className={`p-4 rounded-lg border-2 ${student_documents.photo ? "border-green-500 bg-green-50" : "border-gray-300 bg-gray-50"}`}>
                        <div className="text-center">
                          <User className={`w-12 h-12 mx-auto mb-2 ${student_documents.photo ? "text-green-500" : "text-gray-400"}`} />
                          <h4 className="font-medium">Vesikalık Fotoğraf</h4>
                          {student_documents.photo ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500 mx-auto my-2" />
                              <p className="text-xs text-gray-500">{student_documents.photo.filename}</p>
                              <p className="text-xs text-gray-400">{formatDate(student_documents.photo.uploaded_at)}</p>
                              {student_documents.photo.url && (
                                <a href={student_documents.photo.url} target="_blank" rel="noreferrer" className="text-blue-500 text-xs hover:underline mt-2 block">Görüntüle</a>
                              )}
                            </>
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-400 mx-auto my-2" />
                          )}
                        </div>
                      </div>

                      {/* Diploma */}
                      <div className={`p-4 rounded-lg border-2 ${student_documents.diploma ? "border-green-500 bg-green-50" : "border-gray-300 bg-gray-50"}`}>
                        <div className="text-center">
                          <GraduationCap className={`w-12 h-12 mx-auto mb-2 ${student_documents.diploma ? "text-green-500" : "text-gray-400"}`} />
                          <h4 className="font-medium">Diploma</h4>
                          {student_documents.diploma ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500 mx-auto my-2" />
                              <p className="text-xs text-gray-500">{student_documents.diploma.filename}</p>
                              <p className="text-xs text-gray-400">{formatDate(student_documents.diploma.uploaded_at)}</p>
                              {student_documents.diploma.url && (
                                <a href={student_documents.diploma.url} target="_blank" rel="noreferrer" className="text-blue-500 text-xs hover:underline mt-2 block">Görüntüle</a>
                              )}
                            </>
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-400 mx-auto my-2" />
                          )}
                        </div>
                      </div>

                      {/* Kimlik */}
                      <div className={`p-4 rounded-lg border-2 ${student_documents.id_card ? "border-green-500 bg-green-50" : "border-gray-300 bg-gray-50"}`}>
                        <div className="text-center">
                          <Shield className={`w-12 h-12 mx-auto mb-2 ${student_documents.id_card ? "text-green-500" : "text-gray-400"}`} />
                          <h4 className="font-medium">Kimlik Fotokopisi</h4>
                          {student_documents.id_card ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500 mx-auto my-2" />
                              <p className="text-xs text-gray-500">{student_documents.id_card.filename}</p>
                              <p className="text-xs text-gray-400">{formatDate(student_documents.id_card.uploaded_at)}</p>
                              {student_documents.id_card.url && (
                                <a href={student_documents.id_card.url} target="_blank" rel="noreferrer" className="text-blue-500 text-xs hover:underline mt-2 block">Görüntüle</a>
                              )}
                            </>
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-400 mx-auto my-2" />
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Henüz belge yüklenmemiş</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SÖZLEŞMELER */}
            <TabsContent value="agreements">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className={`w-5 h-5 ${agreements?.agreements_accepted ? "text-green-500" : "text-red-500"}`} />
                    Sözleşme Durumu
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6 p-4 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3">
                      {agreements?.agreements_accepted ? (
                        <>
                          <CheckCircle className="w-8 h-8 text-green-500" />
                          <div>
                            <p className="font-medium text-green-700">Sözleşmeler Kabul Edildi</p>
                            <p className="text-sm text-gray-500">Kabul Tarihi: {formatDate(agreements?.agreements_accepted_at)}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-8 h-8 text-red-500" />
                          <div>
                            <p className="font-medium text-red-700">Sözleşmeler Henüz Kabul Edilmedi</p>
                            <p className="text-sm text-gray-500">Öğrenci henüz sözleşmeleri onaylamamış</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {agreements?.agreements_accepted && (
                    <div className="space-y-4">
                      {agreements?.sales_agreement && (
                        <div className="border rounded-lg">
                          <div className="p-3 bg-blue-50 border-b flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">Satış Sözleşmesi</span>
                          </div>
                          <div className="p-4 max-h-60 overflow-y-auto prose prose-sm" dangerouslySetInnerHTML={{ __html: agreements.sales_agreement }} />
                        </div>
                      )}
                      {agreements?.distance_agreement && (
                        <div className="border rounded-lg">
                          <div className="p-3 bg-purple-50 border-b flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            <span className="font-medium">Mesafeli Satış Sözleşmesi</span>
                          </div>
                          <div className="p-4 max-h-60 overflow-y-auto prose prose-sm" dangerouslySetInnerHTML={{ __html: agreements.distance_agreement }} />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* BİLGİLER */}
            <TabsContent value="info">
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Kişisel Bilgiler</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Ad Soyad</span>
                      <span className="font-medium">{student.name || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">E-posta</span>
                      <span className="font-medium">{student.email || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Telefon</span>
                      <span className="font-medium">{student.phone || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">TC Kimlik No</span>
                      <span className="font-medium">{student.tc_no || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Doğum Tarihi</span>
                      <span className="font-medium">{student.birth_date || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Şehir</span>
                      <span className="font-medium">{student.city || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Eğitim Durumu</span>
                      <span className="font-medium">{student.education_level || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Kayıt Tarihi</span>
                      <span className="font-medium">{formatDate(student.created_at)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Son Giriş</span>
                      <span className="font-medium">{formatDate(student.last_login)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Eğitim & Ödeme Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Atanan Kurslar</span>
                      <span className="font-medium">{student.assigned_courses?.join(', ') || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Mevcut Seviye</span>
                      <span className="font-medium">{student.current_level || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Toplam Ücret</span>
                      <span className="font-medium">{student.total_fee?.toLocaleString('tr-TR') || 0} ₺</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Ödenen</span>
                      <span className="font-medium text-green-600">{student.paid_amount?.toLocaleString('tr-TR') || 0} ₺</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Kalan</span>
                      <span className="font-medium text-red-600">
                        {((student.total_fee || 0) - (student.paid_amount || 0)).toLocaleString('tr-TR')} ₺
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Sözleşme</span>
                      <span className={`font-medium ${student.agreements_accepted ? 'text-green-600' : 'text-red-600'}`}>
                        {student.agreements_accepted ? '✓ Kabul Edildi' : '✗ Kabul Edilmedi'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Seviye Testi</span>
                      <span className={`font-medium ${student.placement_test_completed ? 'text-green-600' : 'text-orange-600'}`}>
                        {student.placement_test_completed ? '✓ Tamamlandı' : '⏳ Bekliyor'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">XP</span>
                      <span className="font-medium">{student.xp || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Danışman Mesajları */}
                {advisor_messages?.length > 0 && (
                  <Card className="col-span-2">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" /> Danışman Mesajları ({advisor_messages.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-64 overflow-auto">
                      <div className="space-y-2">
                        {advisor_messages.map((msg, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${
                            msg.sender_id === studentId ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'
                          }`}>
                            <p className="text-sm">{msg.message || msg.content}</p>
                            <p className="text-xs text-gray-500 mt-1">{formatDate(msg.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileViewer;
